import { useMutation } from '@apollo/client';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import ConfirmDialog from '@website/components/utilities/ConfirmDialog';
import Truncate from '@website/components/utilities/Truncate';
import {
  Currency,
  Gender,
  Maybe,
  RemoveUserFromWaitListDocument,
  Status,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FaEllipsisV } from 'react-icons/fa';
import { CustomToggle } from '../AdminReviews/adminReviews';

export type WaitlistAlgoliaHit = {
  objectID: string;
  created: string;
  id: string;
  spacesRequested?: Maybe<number>;
  groupID: string;
  userID: string;
  groupStartDate: string;
  tripID: string;
  user: {
    id: string;
    givenName: string;
    familyName: string;
    email: string;
    gender: Maybe<Gender>;
  };
  group: {
    id: string;
    start: { utc: string; zone: string };
    guestPrice: { amountZD: number; currency: Currency };
    trip: {
      active: Status;
      description: string;
      id: string;
      title: string;
      urlSlug: string;
      host: { id: string; name: string };
    };
  };
};

export function WaitlistActionCell({
  row,
}: CellContext<WaitlistAlgoliaHit, any>) {
  const [showShowRemoveUserFromWaitlist, setShowRemoveUserFromWaitlist] =
    useState(false);

  const [removeUserFromWaitList, { loading: removingUserFromWaitlist }] =
    useMutation(RemoveUserFromWaitListDocument, {
      update(cache, { data }) {
        const waitListCacheID = cache.identify({
          __typename: 'WaitList',
          id: data.removeUserFromWaitList.id,
        });

        cache.evict({
          id: waitListCacheID,
        });
      },
    });

  return (
    <>
      <ConfirmDialog
        body={
          <Truncate lines={3} enableExpand className="text-muted text-sm">
            Remove {row.original.user.givenName ?? 'customer'}
            from the wait list for {row.original.group.trip.title} on{' '}
            {DateTime.fromISO(row.original.group.start.utc).toLocaleString(
              DateTime.DATETIME_MED,
            )}
            ?
          </Truncate>
        }
        title={`Are you sure you want to remove ${row.original.user?.givenName} from waitlist?`}
        visible={showShowRemoveUserFromWaitlist}
        setVisible={setShowRemoveUserFromWaitlist}
        onConfirmFn={() =>
          removeUserFromWaitList({
            variables: {
              input: {
                groupID: row.original.groupID,
                userID: row.original.userID,
              },
            },
          })
        }
      />

      <Dropdown align={'end'}>
        <Dropdown.Toggle as={CustomToggle} variant="outline-primary">
          <FaEllipsisV />
        </Dropdown.Toggle>

        <Dropdown.Menu flip>
          <Dropdown.Item onClick={() => setShowRemoveUserFromWaitlist(true)}>
            {removingUserFromWaitlist ? 'Removing ...' : 'Remove From Waitlist'}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
}

export const adminWaitlistColumns: ColumnDef<WaitlistAlgoliaHit, any>[] = [
  {
    header: 'Created',
    size: 100,
    cell: ({ row }) => {
      return DateTime.fromISO(row.original.created).toLocaleString(
        DateTime.DATETIME_SHORT,
      );
    },
  },
  {
    header: 'User',
    size: 150,
    cell: ({ row }) => {
      const name = `${row.original.user?.givenName} ${row.original.user?.familyName}`;

      return <Link href={`/me/${row.original.user?.id}`}>{name}</Link>;
    },
  },
  {
    header: 'Trip',
    size: 150,
    cell: ({ row }) => {
      return (
        <Link
          href={{
            pathname: `/trips/${row.original.group.trip.urlSlug}`,
          }}
        >
          {row.original.group.trip.title}
        </Link>
      );
    },
  },
  {
    header: 'Start Date (Group)',
    size: 150,
    cell: ({ row }) => {
      return DateTime.fromISO(row.original.group.start.utc).toLocaleString(
        DateTime.DATETIME_MED,
      );
    },
  },
  {
    header: 'Spaces Requested',
    size: 50,
    cell: ({ row }) => {
      return row.original.spacesRequested;
    },
  },
  {
    header: '',
    id: 'actions',
    size: 50,
    cell: WaitlistActionCell,
  },
];
